import { useEffect, useRef } from 'react';
import { SearchOffers } from '@lambda/commons/apis/elasticsearch/types';
import { PopularProductGroup } from '@lambda/apis/src/product-group/types';
import Loading from '@/components/Loading/Loading';
import OfferCard from '@/components/collections/OfferCard';
import CollectionItemBox from '../../collections/CollectionItemBox';

interface Props {
  offers: Array<SearchOffers>;
  popularProductGroups?: PopularProductGroup[];
  handlePageChange: (value: number) => void;
  pagination?: {
    hasMore: boolean;
    page: number;
  };
  isLoading: boolean;
}

const NoResults = ({
  popularProductGroups,
}: {
  popularProductGroups?: PopularProductGroup[];
}) => (
  <>
    <h3 className="mt-3 flex items-center justify-center text-xl font-bold text-black lg:hidden">
      Search results
    </h3>
    <p className="pt-2 text-center text-sm text-black sm:text-base lg:pt-4 lg:text-left">
      Sorry, no products matched your selection.
    </p>
    <p className="pt-2 text-center text-sm text-black sm:text-base lg:pt-3 lg:text-left">
      Try modifying your search or take a look at some of these recommendations
      below.
    </p>
    <div className="mt-5 grid grid-cols-2 md:grid-cols-3 lg:mt-6 xl:grid-cols-4">
      {popularProductGroups?.map((collection) => (
        <CollectionItemBox
          key={collection.handle}
          link={{
            pathname: `/collections/${collection.handle}`,
          }}
          image={collection.summary?.imageUrl as string}
          title={collection.summary?.title ?? ''}
          price={collection.summary?.totalPrice || 0}
        />
      ))}
    </div>
  </>
);

export default function InfiniteScroll(props: Props) {
  const endOfContainerRef = useRef<HTMLDivElement | null>(null);

  const loadMore = () => {
    if (props?.pagination?.hasMore && !props.isLoading)
      props.handlePageChange(props.pagination.page + 1);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) loadMore();
      },
      {
        root: null,
        rootMargin: '0px 0px 1000px 0px', // Trigger 1000px before the end
        threshold: 0, // Trigger as soon as the target is within the margin
      },
    );

    if (endOfContainerRef.current) observer.observe(endOfContainerRef.current);

    return () => {
      if (endOfContainerRef.current)
        observer.unobserve(endOfContainerRef.current);
    };
  }, [loadMore]);

  if (!props.offers.length)
    return <NoResults popularProductGroups={props.popularProductGroups} />;

  return (
    <>
      <div
        className="mt-5 grid grid-cols-1 gap-2.5 3xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:mt-5 lg:grid-cols-3 xl:grid-cols-4"
        id="e2e-search-results"
      >
        {props.offers.map((offer) => (
          <OfferCard key={offer.key} offer={offer} />
        ))}
      </div>
      {props.isLoading && (
        <div className="pt-6 text-center">
          <Loading />
        </div>
      )}
      <div
        ref={endOfContainerRef} // End marker for IntersectionObserver
        // eslint-disable-next-line react/forbid-dom-props
        style={{ height: '1px' }}
      />
    </>
  );
}
